import nanoid from 'nanoid';
import DateTime from '@newspaces/zero-base/DateTime';
import Model from '@newspaces/zero-base/Model';
import { BlueCredentialType } from '@blueid/access-proto';
import Organisation from './Organisation.js';
import { A as AccessSite } from '../AccessSite-5abc043f.js';
import { A as AccessGroup, n as normalizeFilter } from '../AccessGroup-4a811995.js';
import './AccessObject.js';
import { A as AccessObject } from '../AccessDevice-68531278.js';
import AccessTimeSchedule from './AccessTimeSchedule.js';
import { c as canInsertDocument, a as canRemoveDocuments } from '../canRemoveDocuments-3b897618.js';
import 'nanoid/generate';
import '@newspaces/zero-base/Runtime';
import '@newspaces/zero-base/models/Organisation';
import '@newspaces/zero-base/utils';
import '../AccessCountries-3e45c6a9.js';
import './AccessConfiguration.js';
import '../i18n.js';
import '@newspaces/zero-base/i18n';
import './AccessVendor.js';
import '../parseChangeSet-03074a6b.js';

class AccessCredentialAccess extends Model {
  static Fields = {
    target: {
      type: String,
      required: true,
      enum: ["group", "object"],
      default: "group"
    },
    targetName: {
      type: String,
      required: true,
      description: "The name of the group"
    },
    group: {
      type: AccessGroup,
      required: ({ target }) => target === "group",
      description: "The group this access is bound to"
    },
    groupId: {
      type: Number,
      required: ({ target }) => target === "group",
      description: "The group id this access is bound to"
    },
    object: {
      type: AccessObject,
      required: ({ target }) => target === "object",
      description: "The object this access is bound to"
    },
    objectId: {
      type: Number,
      required: ({ target }) => target === "object",
      description: "The object id this access is bound to"
    },
    isSchedule: {
      type: Boolean,
      description: "If set then this access is only allowed within the credentials schedule"
    },
    type: {
      type: String,
      enum: ["default", "extended", "toggle"],
      default: "default"
    }
  };
  constructor(data) {
    super(AccessCredentialAccess.Fields, data);
  }
}

class AccessCredentialReceiver extends Model {
  static Fields = {
    mobilePhone: {
      type: String,
      format: "mobilePhone"
    },
    email: {
      type: String,
      format: "email",
      required: ({ name, mobilePhone, parent }) => {
        if (parent.identity) {
          return false;
        }
        return !!name && !mobilePhone;
      }
    },
    name: {
      type: String,
      required: ({ email, mobilePhone, parent }) => {
        if (parent.identity) {
          return false;
        }
        return !!email || !!mobilePhone;
      }
    }
  };
  parent;
  constructor(data, parent) {
    super(AccessCredentialReceiver.Fields, data);
    this.parent = parent;
  }
}
Model.decorateModel(AccessCredentialReceiver);

const queryDocument = (filter, _, Runtime) => {
  if (!filter.organisation) {
    return false;
  }
  const organisationUser = Runtime.getOrganisationData(filter.organisation);
  if (!organisationUser) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessCredential.read")) {
    return false;
  }
  return normalizeFilter(Runtime, filter, "site");
};

const insertDocuments = (documents, _, Runtime) => {
  const clearedDocuments = documents.filter((document) => {
    const { organisation: organisationId, credentialType } = document;
    if (!organisationId) {
      return false;
    }
    const hasScope = (scope) => {
      return Runtime.userHasAllScopes(
        organisationId.toString(),
        null,
        scope
      );
    };
    if (!hasScope("accessCredential.insert")) {
      return false;
    }
    const organisationUser = Runtime.getOrganisationData(document.organisation);
    const { customPermissions = {} } = organisationUser;
    const { accessCredentialTypes = [] } = customPermissions;
    if (accessCredentialTypes?.length && !accessCredentialTypes.includes(credentialType)) {
      return false;
    }
    return canInsertDocument(Runtime, document, "site");
  });
  if (!clearedDocuments.length) {
    return false;
  }
  return clearedDocuments;
};

const removeDocuments = async (filter, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  if (!Runtime.userHasAllScopes(organisationId.toString(), null, "accessCredential.remove")) {
    return false;
  }
  return canRemoveDocuments(Runtime, filter, "accessCredentials", "site");
};

const Permissions = {
  insert: [
    {
      who: "owner"
    },
    {
      query: insertDocuments
    }
  ],
  read: [
    {
      who: "owner"
    },
    {
      query: queryDocument
    }
  ],
  edit: false,
  remove: [
    {
      who: "owner"
    },
    {
      query: removeDocuments
    }
  ]
};

class AccessCredential extends Model {
  static CollectionName = "accessCredentials";
  static Permissions = Permissions;
  static Fields = {
    organisation: {
      type: Organisation,
      serialize: "insert"
    },
    site: {
      type: AccessSite,
      required: true,
      serialize: "insert"
    },
    credentialId: {
      type: String,
      min: 10,
      max: 10,
      required: true,
      serialize: "insert",
      unique: "global",
      default: () => nanoid(10),
      description: "The actual credential id for this credential"
    },
    credentialType: {
      type: String,
      enum: ["regular", "maintenance", "master", "nfcWriter", "emergency"],
      serialize: "insert"
    },
    identity: {
      type: String,
      min: 10,
      max: 10,
      description: "Optional identity this credential is bound to",
      serialize: "insert"
    },
    receiver: {
      type: AccessCredentialReceiver
    },
    status: {
      type: String,
      enum: ["pending", "available", "sent", "activated", "expired", "revoked"],
      serialize: "insert"
    },
    name: {
      type: String,
      description: "Optional name for this credential to identify it more quickly"
    },
    description: {
      type: String,
      description: "Optional description for this credential to identify it more quickly"
    },
    activationToken: {
      type: String,
      min: 16,
      max: 16,
      serialize: false,
      deserialize: false
    },
    activationTokenValidUntil: {
      type: DateTime,
      serialize: false,
      deserialize: false
    },
    sentAt: {
      type: DateTime,
      description: "Timestamp when they key has been sent",
      serialize: false
    },
    activatedAt: {
      type: DateTime,
      description: "Timestamp when someone has activated this credential",
      serialize: false
    },
    refreshedAt: {
      type: DateTime,
      description: "Timestamp when this credential was refreshed the last time",
      serialize: false
    },
    requiresRefresh: {
      type: Boolean,
      description: "Set whenever a refresh is required for this credential",
      serialize: false
    },
    validity: {
      type: DateTime,
      description: "The actual current validity of the credential defined by the refresh rate",
      serialize: false
    },
    revokedAt: {
      type: DateTime,
      description: "Set if status is revoked defining when the credential was revoked",
      serialize: false
    },
    revokedBy: {
      type: String,
      description: "Id of the user that caused a revoke if any",
      serialize: false
    },
    validFrom: {
      type: DateTime,
      description: "When this credential will start to become valid",
      serialize: "insert"
    },
    validTo: {
      type: DateTime,
      description: "When this credential will expire",
      serialize: "insert",
      validate: ({ validTo, validFrom }) => {
        if (validTo instanceof DateTime) {
          if (validFrom instanceof DateTime) {
            if (validTo.isBeforeOrSame(validFrom, "minute")) {
              return "mustBeAfterValidFrom";
            }
          }
        }
        return null;
      }
    },
    refresh: {
      type: Number,
      min: 1,
      max: 30,
      default: 30,
      required: ({ credentialType }) => !["emergency", "nfcWriter"].includes(credentialType),
      description: "The amount of days an access stays valid before requiring a refresh",
      serialize: "insert"
    },
    access: {
      type: [AccessCredentialAccess],
      required: ({ credentialType }) => {
        if (credentialType === "regular") {
          return true;
        }
        return false;
      },
      validate: ({ access }) => {
        if (access.length) {
          const duplicates = access.map(
            ({ target, group, object }, index) => !!access.find(
              ({ target: oTarget, group: oGroup, object: oObject }, indexFind) => {
                if (index === indexFind) {
                  return false;
                }
                if (oTarget === target) {
                  if (target === "object")
                    return object?.toString() === oObject?.toString();
                  if (target === "group")
                    return group?.toString() === oGroup?.toString();
                }
                return false;
              }
            )
          );
          if (duplicates.filter((x) => x).length > 0) {
            return "duplicatedEntries";
          }
        }
        return null;
      },
      serialize: "insert"
    },
    accessSchedules: {
      type: [AccessTimeSchedule],
      validate: ({ accessSchedules, access }) => {
        if (access.find(({ isSchedule }) => isSchedule) && !accessSchedules?.length) {
          return "required";
        }
        if (accessSchedules?.length > 4) {
          return "maxFourSchedules";
        }
        return null;
      }
    },
    usageType: {
      type: String,
      enum: ["mobile", "offline"],
      serialize: false
    }
  };
  static TypeColor = {
    regular: "neutral",
    maintenance: "error",
    master: "primary",
    nfcWriter: "warning",
    emergency: "success"
  };
  static StatusColor = {
    pending: "neutral",
    available: "info",
    sent: "primary",
    activated: "success",
    expired: "warning",
    revoked: "error"
  };
  static credentialTypeAsString(type) {
    switch (type) {
      case BlueCredentialType.Regular:
        return "regular";
      case BlueCredentialType.Maintenance:
        return "maintenance";
      case BlueCredentialType.Master:
        return "master";
      case BlueCredentialType.NfcWriter:
        return "nfcWriter";
      case BlueCredentialType.Emergency:
        return "emergency";
      default:
        throw new Error(`CredentialType "${type}" not exists`);
    }
  }
  static credentialTypeAsInt(type) {
    switch (type) {
      case "regular":
        return BlueCredentialType.Regular;
      case "maintenance":
        return BlueCredentialType.Maintenance;
      case "master":
        return BlueCredentialType.Master;
      case "nfcWriter":
        return BlueCredentialType.NfcWriter;
      case "emergency":
        return BlueCredentialType.Emergency;
      default:
        throw new Error(`CredentialType "${type}" not exists`);
    }
  }
  constructor(data) {
    super(AccessCredential.Fields, data);
  }
  get receiverName() {
    return this.receiver?.name;
  }
  // eslint-disable-next-line class-methods-use-this
  getMergeTagProperties() {
    return {
      name: { key: "name" },
      description: { key: "description" },
      receiverName: { key: "receiverName" }
    };
  }
}
Model.declareModel(AccessCredential);

AccessCredential.AccessCredentialAccess = AccessCredentialAccess;
AccessCredential.AccessCredentialReceiver = AccessCredentialReceiver;

export { AccessCredential as default };
//# sourceMappingURL=AccessCredential.js.map
